// Where a product's paid access links live, and what the public product
// document is allowed to say about them.
//
// WHY THEY MOVED. An access link is the product for anything sold as a link: a
// Notion duplicate link, a Canva template, a Google Drive folder. They used to
// sit at `details.accessLinks.links` on products/{id}, which firestore.rules
// makes world-readable (and listable) for every published product, and the
// product page also handed the whole document to its client component, so the
// links were in the page's HTML too. On 2026-10-18 nine live products served
// their paid link to anyone who opened the page, no purchase needed.
//
// WHERE THEY ARE NOW. products/{id}/private/delivery, which only the seller can
// read (firestore.rules). Buyers receive them from the /api/download manifest,
// which runs the same ownership, refund and subscription checks as a file
// download. The public document keeps a stand-in, publicAccessLinks(): the
// count, so the product page can still say "Links: 2" and the dialog knows to
// wait for them, and an always-empty `links` array, because pages left open
// across a deploy read `links.some(...)` and must not crash on a missing field.
//
// THE MOVE. Products written before this keep their links on the public doc
// until the dev console's "Move access links off the public product doc" runs
// (products.privatizeAccessLinks in functions/src/lib/dev-console.ts). Until a
// product has a private document, its legacy public copy is what it delivers
// (resolveAccessLinks), so nothing a buyer paid for goes missing in between.
//
// Pure: no Firestore, no React. The editors, the product page, /api/download
// and the staff tools all read through these.

/** The subcollection under products/{id} that holds seller-only data. */
export const PRODUCT_PRIVATE_COLLECTION = 'private';
/** The document in it that holds the access links. */
export const PRODUCT_DELIVERY_DOC = 'delivery';
/** Most links one product may carry: the editors' limit, enforced again by firestore.rules. */
export const MAX_ACCESS_LINKS = 10;
/** Longest link the editors accept, enforced again by firestore.rules. */
export const MAX_ACCESS_LINK_LENGTH = 2000;

/** What `details.accessLinks` holds on the public product document. */
export type PublicAccessLinks = { enabled: boolean; links: []; count: number };

type Doc = Record<string, unknown>;

function isRecord(v: unknown): v is Doc {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** The links worth storing: strings, trimmed, with blanks dropped. */
export function cleanAccessLinks(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((link): link is string => typeof link === 'string')
    .map((link) => link.trim())
    .filter((link) => link.length > 0);
}

/** The public document's stand-in for `count` links. It never carries a URL. */
export function publicAccessLinks(count: number): PublicAccessLinks {
  return { enabled: count > 0, links: [], count };
}

/**
 * How many links a product delivers, read off a product's `details`.
 *
 * Works on all three shapes that reach it: a legacy public document (the links
 * themselves), the public stand-in (its `count`), and an editor's form, which
 * holds the links the seller is editing. Links that are present win over a
 * count, so a form loaded with a count never reports it after the seller has
 * changed the list. A count that is not a whole number reads as none, and one
 * above the editors' limit reads as the limit, since nothing legitimate writes
 * either.
 */
export function accessLinkCount(details: unknown): number {
  if (!isRecord(details) || !isRecord(details.accessLinks)) return 0;
  const listed = cleanAccessLinks(details.accessLinks.links).length;
  if (listed > 0) return listed;
  const count = details.accessLinks.count;
  if (typeof count !== 'number' || !Number.isInteger(count) || count <= 0) return 0;
  return Math.min(count, MAX_ACCESS_LINKS);
}

/**
 * Whether a product's links have moved to its delivery document, read off the
 * product's `details`: only the public stand-in carries a `count` (every save
 * since the move writes one, and so does the dev console's move). A legacy
 * document still holds its links itself.
 */
export function hasMovedAccessLinks(details: unknown): boolean {
  return isRecord(details) && isRecord(details.accessLinks) && typeof details.accessLinks.count === 'number';
}

/**
 * The links a product delivers.
 *
 * `delivery` is the private document's data, or null/undefined when it does not
 * exist. Once it exists it is the only authority, including when it holds no
 * links: a seller who removed them all must not have a stale public copy
 * delivered instead. Before it exists, the legacy public copy is what the
 * product has always delivered.
 */
export function resolveAccessLinks(product: unknown, delivery: unknown): string[] {
  if (isRecord(delivery)) return cleanAccessLinks(delivery.links);
  const details = isRecord(product) ? product.details : null;
  if (!isRecord(details) || !isRecord(details.accessLinks)) return [];
  return cleanAccessLinks(details.accessLinks.links);
}

/**
 * A copy of a product document that is safe to hand to a page anyone can
 * request: its access links replaced by the public stand-in. The caller's
 * document is not modified, and one with no links section comes back as is.
 */
export function withoutAccessLinkUrls<T>(product: T): T {
  if (!isRecord(product) || !isRecord(product.details) || !isRecord(product.details.accessLinks)) {
    return product;
  }
  return {
    ...product,
    details: { ...product.details, accessLinks: publicAccessLinks(accessLinkCount(product.details)) },
  } as T;
}

/**
 * The `accessLinks` an editor's form opens on: the links the product delivers,
 * with `enabled` derived from them as every save derives it. The stored count
 * is dropped, because it would go stale the moment the seller edits the list.
 */
export function editorAccessLinks(stored: unknown, links: readonly string[]): { enabled: boolean; links: string[] } {
  const { count: _count, ...rest } = isRecord(stored) ? stored : {};
  return { ...rest, enabled: links.length > 0, links: [...links] };
}

/**
 * The `accessLinks` the edit form takes from a parked revision or a local draft.
 *
 * A copy carrying a `count` was saved by an editor running the code from before
 * the move: that editor built its form from the public stand-in, so it never
 * saw the product's links, and its list holds only links it added. Taken as it
 * is, the form would open with the links gone and the next save would empty the
 * delivery document. So the delivered links come back, ahead of the ones it
 * added. A copy with no count is this editor's own (editorAccessLinks drops the
 * count, and nothing puts it back) and is returned untouched, removals included.
 */
export function mergeHiddenAccessLinks(overlay: unknown, delivered: readonly string[]): unknown {
  if (!isRecord(overlay) || overlay.count === undefined) return overlay;
  const links = [...new Set([...cleanAccessLinks([...delivered]), ...cleanAccessLinks(overlay.links)])];
  return editorAccessLinks(overlay, links);
}

/**
 * A product write for rules that predate the move: the links on the product
 * document, as every save wrote them before. The editors fall back to it only
 * when their batch with the delivery document is refused, which is what rules
 * without that document's match do. Once those rules are deployed they refuse
 * links on the product document (publicAccessLinks in firestore.rules), so this
 * can never put links back on a product after the move. Fields with no `details`
 * map come back as they are: the write replaces `details` whole, so inventing
 * one would wipe the stored map.
 */
export function withLegacyAccessLinks<T extends Record<string, unknown>>(fields: T, links: readonly string[]): T {
  if (!isRecord(fields.details)) return fields;
  return { ...fields, details: { ...fields.details, accessLinks: { enabled: links.length > 0, links: [...links] } } };
}
